import {
  booleanAttribute,
  Component,
  DoCheck,
  Inject,
  inject,
  Injector,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { noop } from 'rxjs';
import { getErrors, provideValidator, provideValueAccessor } from '@bspk/ngx-ui/lib/util';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BspkInlineAlert } from '@bspk/ngx-ui/lib/inline-alert';
import { BspkRadioOption, RadioOptionSize } from '../radio-option';

export type RadioGroupOption<TValue> = {
  label: string;
  value: TValue;
  description?: string;
  disabled?: boolean;
};

@Component({
  selector: 'bspk-radio-group',
  imports: [BspkRadioOption, BspkInlineAlert],
  templateUrl: './radio-group.component.html',
  styleUrl: './radio-group.component.scss',
  host: {
    '[attr.data-radio-group]': 'true',
    '[attr.data-control-group]': 'true',
    role: 'radiogroup',
  },
  providers: [provideValueAccessor(BspkRadioGroup), provideValidator(BspkRadioGroup)],
})
export class BspkRadioGroup<TValue> implements ControlValueAccessor, Validator, DoCheck, OnInit {
  readonly options = input.required<RadioGroupOption<TValue>[]>();
  readonly name = input.required<string>();
  readonly selectedValue = model<TValue>();
  readonly size = input<RadioOptionSize>('base');
  readonly required = input(false, { transform: booleanAttribute });
  readonly errorState = signal<string | SafeHtml>('');

  private readonly injector = inject(Injector);
  private readonly sanitizer = inject(DomSanitizer);

  private ngControl!: NgControl | null;

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
  }

  onChange: (value: any) => void = noop;
  onTouched: () => void = noop;

  writeValue(obj: any) {
    this.selectedValue.set(obj);
  }

  setDisabledState(isDisabled: boolean) {
    // Noop
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onCheckedChange(value: TValue, checked: boolean) {
    if (checked) {
      this.selectedValue.set(value);
      this.onChange(value);
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    // Noop
  }

  validate(): ValidationErrors | null {
    if (this.required() && this.selectedValue() == null) {
      return { required: true };
    }
    return null;
  }

  ngDoCheck() {
    if (this.ngControl?.control) {
      this.errorState.set(getErrors(this.ngControl.control, this.sanitizer));
    }
  }
}
